import { SearchFilterCondition, SearchUserEnriched } from 'lib-search-types';
import { useMemo } from 'react';

import { searchApi } from 'apis/search';
import { useMembersWithPresence } from 'hooks/usePresenceCacheSync';

type UseTransferAgentsResult = {
  members: SearchUserEnriched[];
  isLoading: boolean;
  isError: boolean;
};

/**
 * Returns the agents currently available for transfer, kept live.
 *
 * Fetches *all* members (not just online ones) enriched with workload config,
 * merges real-time presence-overlay updates, and returns the subset that is
 * online. Fetching the full set — rather than filtering `is_online` server-side
 * — is what lets agents appear and disappear from the list as their presence
 * changes while it is open, instead of showing a snapshot frozen at fetch time.
 *
 * Pass `enabled = false` to skip the request while the surface is closed. Mount
 * `TransferPresenceLiveUpdates` alongside (only while open) to keep the overlay
 * fed with live presence events.
 */
export const useTransferAgents = (
  enabled: boolean,
  queueId?: string | null,
): UseTransferAgentsResult => {
  const filters: SearchFilterCondition<'users'>[] = useMemo(() => {
    const base: SearchFilterCondition<'users'>[] = [
      { field: 'user_type', operator: 'is_one', values: [{ value: 'member' }] },
    ];
    return queueId
      ? [
          ...base,
          {
            field: 'queues',
            operator: 'includes_one',
            values: [{ value: queueId }],
          },
        ]
      : base;
  }, [queueId]);

  const { allMembers, isLoading, isError } =
    searchApi.useReadSearchUsersEnrichedQuery(
      {
        scope: 'users',
        filters: { strategy: 'all', filters },
        sortBy: { field: 'display_name', order: 'asc' },
        pageSize: 1000,
        enrichmentSpec: ['workload_config'],
      },
      {
        skip: !enabled,
        refetchOnMountOrArgChange: true,
        selectFromResult: ({ data, isLoading, isError }) => ({
          allMembers: data?.data.results.map(({ result }) => result) ?? [],
          isLoading,
          isError,
        }),
      },
    );

  const membersWithPresence = useMembersWithPresence(allMembers);

  const members = useMemo(
    () => membersWithPresence.filter((member) => member.is_online),
    [membersWithPresence],
  );

  return { members, isLoading, isError };
};
